import { memo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Typography } from "@mui/material"
import { ProductsTableItem } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { ProductCardTitle } from "./ProductCardTitle"
import { ProductDetailsDialog } from "./ProductDetailsDialog"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { Tag } from "./Tag"

const useStyles = makeStyles()((theme) => ({
    tagLine: {
        display: "flex",
        alignItems: "center",
        gap: "0.375rem",
        flexWrap: "wrap",
    },
    onlyLeftTag: {
        display: "inline-flex",
        alignItems: "center",
        padding: `0 ${theme.spacing(1)}`,
        color: theme.palette.common.white,
        backgroundColor: theme.palette.common.black,
        borderRadius: "0.25rem",
    },
    staffOnlyTag: {
        display: "inline-flex",
        alignItems: "center",
        padding: `0 ${theme.spacing(1)}`,
        color: theme.palette.tailwind.amber[800],
        backgroundColor: theme.palette.tailwind.amber[100],
        border: `1px solid ${theme.palette.tailwind.amber[300]}`,
        borderRadius: "0.25rem",
    },
}))

type ProductDetailsContentProps = {
    product: ProductsTableItem
    searchRegex: RegExp | undefined
    marketingTag?: string
    productsOnSale?: number
    userId: number
    onFavoriteToggle: (productId: number, makeFavorite: boolean) => Promise<boolean>
}

export const ProductDetailsContent = memo(
    ({ product, searchRegex, marketingTag, productsOnSale, userId, onFavoriteToggle }: ProductDetailsContentProps) => {
        const { classes } = useStyles()
        const [productId, setProductId] = useState<number | null>(null)

        const handleDetailsClick = () => {
            setProductId(product.id)
        }

        // The catalog only ever hands an admin-only product to a merchant, so the tag needs no
        // extra role check of its own.
        const showTagLine = Boolean(marketingTag || productsOnSale || product.isAdminOnly)

        return (
            <>
                {showTagLine && (
                    <Box className={classes.tagLine}>
                        {marketingTag && <Tag>{marketingTag}</Tag>}
                        {!!productsOnSale && (
                            <Box className={classes.onlyLeftTag}>
                                <LocalizedStrict id="offer-tag-limited-on-sale" vars={{ amount: productsOnSale }}>
                                    <Typography variant="caption">{productsOnSale} left</Typography>
                                </LocalizedStrict>
                            </Box>
                        )}
                        {product.isAdminOnly && (
                            <Box className={classes.staffOnlyTag}>
                                <LocalizedStrict id="create-order-product-staff-only-tag">
                                    <Typography variant="caption">Staff only</Typography>
                                </LocalizedStrict>
                            </Box>
                        )}
                    </Box>
                )}
                <ProductCardTitle name={product.name} searchRegex={searchRegex} onInfoClick={handleDetailsClick} />

                {productId && (
                    <ProductDetailsDialog
                        userId={userId}
                        productId={productId}
                        isFavorite={!!product.userDetails?.isFavorite}
                        onFavoriteToggle={onFavoriteToggle}
                        handleClose={() => setProductId(null)}
                    />
                )}
            </>
        )
    },
)
